/**
 * Auto-detect user's language based on their country (IP geolocation).
 *
 * Reuses existing ipapi.co service (same as OrganizationForm.tsx and Registration.tsx).
 */

import { LOCALES } from '../i18n/locales';
import logger from './logger';

/**
 * Map country code to supported language.
 *
 * Only 3 languages supported: BG, TR, EN
 * - Bulgaria → Bulgarian
 * - Turkey → Turkish
 * - All others → English (default)
 */
const getLanguageFromCountryCode = (countryCode: string): string => {
    const countryToLanguage: Record<string, string> = {
        'BG': LOCALES.BULGARIAN,
        'TR': LOCALES.TURKISH,
    };

    return countryToLanguage[countryCode] || LOCALES.ENGLISH;
};

/**
 * Detect language from user's IP address (first-time visitors only).
 *
 * Priority:
 * 1. Saved language preference (localStorage) - highest priority
 * 2. IP-based country detection - for first visits
 * 3. English - fallback
 */
export const initializeLanguage = async (): Promise<string> => {
    // Check for saved preference first
    const savedLanguage = localStorage.getItem('dentobook-lang');
    if (savedLanguage) {
        return savedLanguage;
    }

    // First-time visitor - detect from IP
    try {
        const response = await fetch('https://ipapi.co/json/');
        const data = await response.json();
        const countryCode = data?.country;

        if (!countryCode) {
            logger.info('Country not detected, defaulting to English');
            return LOCALES.ENGLISH;
        }

        const detectedLanguage = getLanguageFromCountryCode(countryCode);
        logger.info(`Country: ${countryCode} → Language: ${detectedLanguage}`);

        // Save for next visit
        localStorage.setItem('dentobook-lang', detectedLanguage);

        return detectedLanguage;
    } catch (error) {
        logger.error('IP detection failed, defaulting to English:', error);
        return LOCALES.ENGLISH;
    }
};
